<template>
    <div class="card flex-grow-1">
        <div class="card-body panel-card-body d-flex flex-column">
            <div
                class="rich-text-content mb-3"
                v-show="panel.sub_header !== '' && panel.sub_header != '<p></p>'"
                v-html="panel.sub_header"
            ></div>

            <!-- Score Display -->
            <div v-if="panel.display_score" class="mx-auto mt-5">
                <p class="text-center text-secondary mb-3 h5">{{ labelsTranslated.yourScore }}</p>
                <p class="display-4 mb-0 text-center text-primary">
                    <span>{{ session.score }}</span>
                    <span v-show="session.score_max !== null"
                        >&nbsp;<span style="vertical-align: 2px">/</span> {{ session.score_max }}</span
                    >
                </p>
                <p class="text-center mt-3 medium" v-show="panel.display_pass_fail">
                    <span v-show="session.successful === true" class="text-success font-weight-500">{{ labelsTranslated.successful }}</span>
                    <span v-show="session.successful === false" class="text-danger font-weight-500">{{ labelsTranslated.unsuccessful }}</span>
                </p>
                <br />
                <br />
                <br />
            </div>

            <div v-else-if="panel.display_pass_fail" class="mx-auto mt-4 mb-5">
                <span v-show="session.successful === true" class="text-success font-weight-500">{{ labelsTranslated.successful }}</span>
                <span v-show="session.successful === false" class="text-danger font-weight-500">{{ labelsTranslated.unsuccessful }}</span>
            </div>

            <!-- Score Breakdown -->
            <div class="mt-1">
                <div v-if="panel.display_breakdown" class="d-flex align-items-center mb-4">
                    <div class="flex-grow-1">
                        <p class="h6 align-middle mb-0">{{ labelsTranslated.responseSummary }}</p>
                    </div>
                    <div class="flex-grow-0 d-flex flex-wrap justify-content-end align-items-center">
                        <button @click="openUsernameModal()" class="btn btn-link btn-sm mr-2" v-if="allowUsernameInput">
                            <span class="icon icon-100 icon-glyphicons-basic-pencil mr-1"></span>
                            <span>Your Name</span>
                        </button>
                        <info-popover
                            :content="downloadBtnPopoverTxt"
                            placement="top"
                        >
                            <button @click="downloadSessionSummary()" :disabled="!enablePDFDownloadBtn" class="btn btn-light text-primary badge-highlight">
                                <span class="icon icon-lg icon-glyphicons-basic-cloud-download mr-2"></span>
                                <span>{{ labelsTranslated.downloadPDF }}</span>
                            </button>
                        </info-popover>
                    </div>
                </div>
                <learner-results-summary
                    :results="session.session_results"
                    :language-code="sessionLanguageCode"
                    :show-breakdown="panel.display_breakdown"
                    :show-timers="panel.display_breakdown"
                    :show-notes="panel.include_learner_notes"
                    :show-chat-logs="true"
                ></learner-results-summary>
            </div>
        </div>

        <!-- Username Modal -->
        <base-modal id="report-name-modal" :loading="modalLoading" size="medium" :centered="true">
            <template slot="title">Add Your Name</template>
            <template>
                <p class="mb-4">
                    Optionally provide a name to be displayed on your Learner Results PDF.
                </p>
                <base-form
                    v-model="anonymousUser"
                    :fields="anonymousUserFields"
                    :inline-style="true"
                    ref="reportNameForm"
                ></base-form>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button @click="saveUsername()" :disabled="modalLoading" class="btn btn-primary" type="button">
                    Submit
                </button>
            </template>
        </base-modal>
    </div>
</template>

<script>
import http from "@/axios.js"
import Utilities from "@/assets/js/utilities.js"
import BaseForm from "@/components/BaseForm"
import BaseModal from "@/components/BaseModal"
import LearnerResultsSummary from "@/components/LearnerResultsSummary"
import InfoPopover from "@/components/InfoPopover"

export default {
    name: "ScorePanel",
    components: {
        BaseForm,
        BaseModal,
        LearnerResultsSummary,
        InfoPopover,
    },
    props: {
        panel: {
            type: Object,
            required: true,
        },
        session: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            anonymousUser: {
                username: "",
            },
            anonymousUserFields: [
                {
                    name: "Name",
                    key: "username",
                    type: "text",
                    placeholder: "Your name",
                    maxlength: 200,
                    required: true,
                },
            ],
            modalLoading: false,
        }
    },
    computed: {
        allowUsernameInput() {
            // Only allow username input when the learner is using a "publish link".
            return this.session.share_link && this.session.share_link.is_publish_link
        },
        sessionLanguageCode() {
            return this.$store.getters.sessionLanguageCode
        },
        labelsTranslated() {
            return this.$store.getters.sessionLabelsTranslated
        },
        enablePDFDownloadBtn() {
            let sessionCase = this.$store.getters.sessionCase
            let isCommunitySession = this.$store.getters.session.is_community_session
            if (isCommunitySession) {
                // For community sessions, only show the PDF download button if published before 2025.
                let isPublishedAfter2025 = new Date(sessionCase.community_published_datetime) > new Date('2025-01-01T00:00:00Z')
                return !isPublishedAfter2025
            }
            return sessionCase.allow_results_pdf_download
        },
        downloadBtnPopoverTxt() {
            return this.enablePDFDownloadBtn ? "" : "A downloadable PDF isn't available for this learning experience when accessed via the Community"
        },
    },
    methods: {
        // Open a new tab with the  response summary.
        downloadSessionSummary() {
            window.open(
                `/export/sessions/${this.session.id}/response-summary?includeNotes=${this.panel.include_learner_notes}`,
                "_blank"
            )
        },
        formatStopwatch(seconds) {
            return Utilities.formatStopwatch(Math.round(seconds))
        },
        openUsernameModal() {
            $("#report-name-modal").modal("show")
        },
        saveUsername() {
            if (!this.$refs.reportNameForm.validate()) {
                return
            }
            this.modalLoading = true
            let vm = this
            http.put(`/api/sessions/${this.session.id}/username/`, {
                username: this.anonymousUser.username,
            })
                .then((response) => {
                    vm.$store.commit("setSessionUsername", response.data.anonymous_username)
                    $("#report-name-modal").modal("hide")
                })
                .finally(() => {
                    vm.modalLoading = false
                })
        },
    },
}
</script>
